/** Estilos compartidos Sonos/Audio — negro predominante + glow puntual (look Samsung). */
export const SONOS_BLUE = '#2391FF';
export const SONOS_BG = '#0a0a0a';
export const SONOS_CARD = '#141414';
export const SONOS_BORDER = '#333';

/** Superficies sutiles sobre negro (sin teñir toda la UI de azul/gris). */
export const SURFACE = 'rgba(255,255,255,0.03)';
export const SURFACE_BORDER = 'rgba(255,255,255,0.08)';
export const MUTED = 'rgba(255,255,255,0.45)';
export const PRESS_TRANSITION =
  'transform 140ms cubic-bezier(0.2, 0.9, 0.3, 1), background-color 160ms ease, border-color 160ms ease, box-shadow 180ms ease, color 160ms ease, opacity 90ms ease';

/** Barra de título: negro + glow azul puntual (como Samsung, sin teñir todo el modal). */
export const HEADER_GRADIENT = `
  radial-gradient(120% 140% at 50% 0%, ${SONOS_BLUE}2e 0%, transparent 62%),
  linear-gradient(180deg, #161616 0%, #0a0a0a 100%)
`;

/** Halo suave solo arriba del sheet/panel; el cuerpo queda negro. */
export const AMBIENT_GLOW =
  'radial-gradient(95% 40% at 50% -6%, rgba(35,145,255,0.14) 0%, transparent 48%)';

/** Fondo de cards: negro. */
export const CARD_GLOW_BG = '#0a0a0a';

export const CARD_SURFACE_SX = {
  border: `1px solid ${SURFACE_BORDER}`,
  borderRadius: '16px',
  backgroundColor: CARD_GLOW_BG,
} as const;

export const SECTION_TITLE_SX = {
  color: MUTED,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
};

export const sonosSelectFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#0a0a0a',
    height: '32px',
    '& fieldset': { borderColor: SURFACE_BORDER },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
    '&.Mui-focused fieldset': { borderColor: SONOS_BLUE, borderWidth: '1px' },
  },
  '& .MuiSelect-select': {
    color: 'white',
    fontSize: '0.8rem',
    padding: '6px 8px !important',
  },
  '& .MuiInputLabel-root': {
    color: MUTED,
    fontSize: '0.75rem',
    transform: 'translate(8px, 7px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(8px, -7px) scale(0.75)',
    },
  },
};

export const sonosMenuPaperSx = {
  bgcolor: '#121212',
  color: '#fff',
  border: `1px solid ${SURFACE_BORDER}`,
  borderRadius: '12px',
  '& .MuiMenuItem-root': {
    fontSize: '0.8rem',
    '&:hover': { backgroundColor: 'rgba(35,145,255,0.14)' },
    '&.Mui-selected': { backgroundColor: 'rgba(35,145,255,0.22)' },
  },
};

export const AUDIO_INNER_SCROLL_SEL = '[data-audio-inner-scroll]';
export const AUDIO_NO_SWIPE_SEL = '[data-audio-no-swipe]';

export const sonosSwitchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: SONOS_BLUE },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: SONOS_BLUE },
};

export const sonosPillButtonSx = {
  borderRadius: 999,
  textTransform: 'none' as const,
  fontSize: 13,
  fontWeight: 700,
  minHeight: 40,
  py: 0.75,
  px: 2,
};

export const sonosConfigSectionTitleSx = {
  ...SECTION_TITLE_SX,
  mb: 0.75,
  mt: 2,
};

export const sonosDialogPaperSx = {
  bgcolor: '#0a0a0a',
  color: '#fff',
  borderRadius: '16px',
  border: `1px solid ${SURFACE_BORDER}`,
};

export const sonosConfigRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  py: 1,
  borderBottom: `1px solid ${SURFACE_BORDER}`,
};

/** Feedback táctil sutil en iconos del reproductor/cola. */
export const sonosIconTapSx = {
  transition: PRESS_TRANSITION,
  WebkitTapHighlightColor: 'transparent',
  '@media (hover: hover)': {
    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
  },
  '&:active': {
    bgcolor: 'rgba(35,145,255,0.22)',
    transform: 'scale(0.93)',
  },
  '&.Mui-disabled': {
    transform: 'none',
  },
};

export const sonosIconTapDangerSx = {
  transition: PRESS_TRANSITION,
  WebkitTapHighlightColor: 'transparent',
  '@media (hover: hover)': {
    '&:hover': { bgcolor: 'rgba(229,57,53,0.12)' },
  },
  '&:active': {
    bgcolor: 'rgba(229,57,53,0.28)',
    color: '#ff8a80',
    transform: 'scale(0.93)',
  },
  '&.Mui-disabled': {
    transform: 'none',
  },
};

export const sonosTextButtonTapSx = {
  transition: 'background-color 0.1s ease, color 0.1s ease',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    bgcolor: 'rgba(255,255,255,0.06)',
  },
};

/** Cascarón sheet inferior: negro + acento azul solo en el borde superior. */
export const modalSheetShellSx = {
  borderRadius: '16px 16px 0 0',
  borderTop: `2px solid ${SONOS_BLUE}`,
  borderLeft: `1px solid ${SURFACE_BORDER}`,
  borderRight: `1px solid ${SURFACE_BORDER}`,
  boxShadow: '0 -8px 28px rgba(0, 0, 0, 0.55)',
  backgroundColor: '#000',
  backgroundImage: AMBIENT_GLOW,
};

export const modalSheetHandleSx = {
  height: '28px',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  touchAction: 'pan-y',
  cursor: 'grab',
  WebkitTapHighlightColor: 'transparent',
  userSelect: 'none',
  '&:active': {
    cursor: 'grabbing',
  },
};

export const modalSheetHandleBarSx = {
  width: '40px',
  height: '4px',
  backgroundColor: 'rgba(255,255,255,0.28)',
  borderRadius: '2px',
  mt: 0.75,
};

/** Header de paneles / sheets (mismo en H y V). */
export const sonosPanelHeaderSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  px: 1,
  py: 0.45,
  borderBottom: `1px solid ${SURFACE_BORDER}`,
  background: HEADER_GRADIENT,
  flexShrink: 0,
  minHeight: 36,
};

/** Título en header horizontal (fila con flex). No usar solo en columna sin override de flex. */
export const sonosPanelTitleSx = {
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.82rem',
  textAlign: 'center' as const,
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
  px: 0.5,
  lineHeight: 1.15,
};

export const sonosListRowSx = {
  border: `1px solid ${SURFACE_BORDER}`,
  borderRadius: '12px',
  backgroundColor: CARD_GLOW_BG,
};
